import CompanyUpdateRequest from "./models/CompanyUpdateRequest"
import ItemCollection from "./models/ItemCollection"
import Item from "./models/Item"
import UserCollections from "./models/UserCollections"

const processes = [
  { value: 'bulk-update', title: 'Bulk Update' },
  { value: 'showcase', title: 'Portfolio Showcase' },
  { value: 'company-updates', title: 'Company Updates' },
  { value: 'save-as-collection', title: 'Save as New Collection' },
]

export const useCollectionsStore = defineStore('collections', {
  state: () => {
    return {
      initialized: false,
      system: {
        favorites: new ItemCollection({ slug: 'favorites' }),
        selection: new ItemCollection({ slug: 'selection' })
      } as Record<string, ItemCollection>,
      userCollections: new UserCollections([], {}, {}),
      newCollection: new ItemCollection(),
      userCollection: new ItemCollection(),
      editCollectionDialog: useDialog('edit-collection-dialog'),
      processing: false,
      processDialog: useDialog('processing-dialog'),
      processingSelection: '',
      processOptions: {
        showcaseTitle: '',
        companyUpdateCustomMessage: false,
        companyUpdateSubject: '',
        companyUpdateMessage: ''
      },
      processResult: undefined as any | undefined,
      progress: useProgress(),
    }
  },
  actions: {
    // Initialize the store
    async init() {
      if (this.initialized) return

      // Load collections
      await this.loadCollections()
      // Mark as initialized
      this.initialized = true
    },
    // Load the user's collections
    async loadCollections() {
      // Does the user have a loaded collection?
      const slug = this.user.prefs.get('selection') || 'selection'
      this.system.selection = new ItemCollection({ slug })
      // Fetch the collections
      return Promise.all([
        this.system.favorites.fetch(),
        this.system.selection.fetch()
      ])
    },

    // --------------
    // Collections ==
    // --------------
    //

    // Add an item to a collection
    addToCollection(collection: string, item: CollectionItem) {
      // Do not add if the item is already in the collection
      if (this.system[collection].exists({ slug: item.slug })) return Promise.resolve()

      // Increment progress counter in case it's part of a process
      this.progress.makeProgress(`Adding ${item.name} to ${collection}`)
      // Set the collection slug from the given collection
      item.collection_slug = this.system[collection].get('slug')
      // Create and save the item in the given collection
      return Item.create(item, this.system[collection].collectionItems).save()
    },

    // Remove an item from a collection
    removeFromCollection(collection: string, item: CollectionItem) {
      // Increment progress counter in case it's part of a process
      this.progress.makeProgress(`Removing ${item.name} from ${collection}`)
      // Locate the item in the collection
      const foundItem = this.system[collection].collectionItems.find({ slug: item.slug })
      // Bail if the item is not found
      if (!foundItem) return Promise.resolve()
      // Remove the item from the collection
      return foundItem.delete()
    },

    // Fetch the user's collections
    fetchUserCollections() {
      return this.userCollections.fetch()
    },

    // Fetch a collection
    fetchCollection(slug: string) {
      this.userCollection = new ItemCollection({ slug })
      return this.userCollection.fetch()
    },

    // Save a new collection from the current selection
    saveAsCollection() {
      this.newCollection = new ItemCollection()
      this.processDialog.open()
    },

    // Create a new collection from the current selection
    // Note: attaching items to the new collection is handled on the back end by
    // loading the current selection and renaming it with the new name, description,
    // and public flag.
    createCollection() {
      // Save the current selection as a new collection
      return this.userCollection.save()
    },

    // Edit a collection
    editCollection(slug: string) {
      const collection = this.userCollections.find({ slug })
      if (!collection) return

      // Set the form collection
      this.userCollection = collection as ItemCollection
      this.editCollectionDialog.open()
    },

    // Delete a collection
    deleteCollection(slug: string) {
      const collection = this.userCollections.find({ slug })
      if (!collection) return Promise.resolve()
      // Delete the collection
      return collection.delete()
    },

    // ------------
    // Favorites ==
    // ------------

    // Add a favorite
    addFavorite(favorite: any) {
      return this.addToCollection('favorites', favorite)
    },

    // Remove a favorite
    removeFavorite(favorite: any) {
      return this.removeFromCollection('favorites', favorite)
    },

    // ------------
    // Selection ==
    // ------------

    // Add an item to the selection
    addSelection(item: any) {
      return this.addToCollection('selection', item)
    },

    // Remove a favorite
    removeSelection(item: any) {
      return this.removeFromCollection('selection', item)
    },

    // Clear the selection
    clearSelection() {
      return Promise.all(this.system.selection.models.map((item: any) => {
        this.removeSelection(item)
      }))
    },

    // Load a collection
    loadSelection(slug: string) {
      // Set the prefs to the collection
      const user = useUserStore()
      user.prefs.set('selection', slug)
      return user.prefs.save().then(() => {
        this.system.selection = new ItemCollection({ slug })
        this.system.selection.fetch()
      })
    },

    // Unload the selection
    unloadSelection() {
      this.loadSelection('selection')
    },

    // -------------
    // Processing ==
    // -------------

    validateSelection(validator: (item: Item) => string) {
      // Iterate over the selection and validate each item
      this.system.selection.models.map((item: any) => {
        // Validate the item
        const error = validator(item)
        // Set the error on the item
        item.set('error', error)
      })
    },

    resetProcessing() {
      // Reset the processing dialog
      this.processDialog.close()
      // Reset the processing state
      this.progress.reset()
      this.processingSelection = ''
      this.processResult = undefined
      // Reset the selection validation
      this.validateSelection(() => '')
    },

    async executeProcess() {
      // Reset results
      this.processResult = undefined
      // Reset progress
      this.progress.reset()
      // Set processing flag to true
      this.processing = true
      // Execute the process
      switch (this.processingSelection) {
        case 'showcase':
          await this.showcaseProcess()
          break
        case 'company-updates':
          await this.companyUpdatesProcess()
          break
        default:
          console.error('Unknown process selected')
      }
      // Show the results
      this.processing = false
    },

    // Showcase Process
    //
    // Send a request to the Rebrandly API to create a short link
    // for the portfolio showcase page
    //
    async showcaseProcess() {
      this.progress.reset()
      // Add the processing tally
      const estimate = 3
      this.progress.addWork(estimate)
      // Get the slugs and title
      this.progress.makeProgress('Building link')
      const slugs = this.system.selection.models.map((item: any) => item.route.params.slug).join(',')
      const title = this.processOptions.showcaseTitle ? encodeURIComponent(this.processOptions.showcaseTitle) : ''
      // Build the link
      let longLink = `https://sosv.com/portfolio-showcase/?companies=${slugs}`
      if (title.length) {
        longLink += `&title=${title}`
      }
      // Convert the link to a short link
      this.progress.makeProgress('Making short link')
      const config = useRuntimeConfig()
      const domainId = config.public.rebrandlyDomainId
      const { data } = await useFetch(
        'https://api.rebrandly.com/v1/links',
        {
          method: 'post',
          headers: {
            apiKey: config.public.rebrandlyApiKey as string,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            destination: longLink,
            domain: {
              id: domainId,
              ref: `/domains/${domainId}`
            }
          })
        }
      )
      const shortLink = data.value as Record<'shortUrl', string>
      // Set the result
      this.processResult = shortLink.shortUrl
    },

    // Company Updates Process
    //
    // Send a request for a company update to the selected companies
    //
    async companyUpdatesProcess() {
      const report: string[] = []
      this.progress.reset()
      // Add the processing tally
      const estimate = this.system.selection.length
      this.progress.addWork(estimate)
      // Make the requests
      await Promise.all(this.system.selection.models.map(async (item: any) => { //new CompanyUpdateRequest({
        // Skip if the company is not accepting updates
        if (!item.item_attributes.reporting) {
          // console.log('Not accepting updates', item.name)
          report.push(`${item.name} - Not accepting updates`)
          return
        }
        // Make the request
        const request = new CompanyUpdateRequest({
          company_slug: item.route.params.slug
        })
        await request.save()
          // Report success or error
          .then(() => report.push(`${item.name} - Request sent`))
          .catch((error: any) => report.push(`${item.name} - Error: ${error.message}`))
        // Update progress
        this.progress.makeProgress(`Request sent for ${item.name}`)
      }))
      // Set the result
      this.processResult = report
    },
  },

  getters: {
    // User
    user: () => useUserStore(),
    // Processing
    processItems(): ProcessItem[] {
      const isLoadedFromSelection = this.isLoadedFromSelection
      // Utility function to filter out any unavailable processes
      return processes.filter((item: ProcessItem) => {
        // Skip adding a new collection if we are currently loaded into the selection
        if (item.value === 'save-as-collection' && !isLoadedFromSelection) return false
        // Everything OK
        return true
      })
    },
    processName: (state) => processes.find((item: any) => item.value === state.processingSelection)?.title,
    // Favorites
    favorites: (state) => state.system.favorites,
    hasFavorites: (state) => state.system.favorites.models.length > 0,
    isFavorite: (state) => (favorite: CollectionItem | undefined) => {
      // Return false if no favorite is provided
      if (!favorite) return false
      // Check if the favorite exists in the user's favorites
      return state.system.favorites.exists({ slug: favorite.slug })
    },
    // Selection
    selection: (state) => state.system.selection,
    selectionName: (state) => state.system.selection.get('name'),
    selectionSlug: (state) => state.system.selection.get('slug'),
    hasSelection: (state) => state.system.selection.models.length > 0,
    isInSelection: (state) => (item: CollectionItem | undefined) => {
      if (!item) return false
      return state.system.selection.exists({ slug: item.slug })
    },
    isLoadedFromSelection: (state) => state.system.selection.get('name') === 'Selection',
    processStatus: (state) => state.progress.status,
    processProgressPercent: (state) => state.progress.percent
  }
})
